import { useCallback, useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';

export interface EmailPreferences {
  invite_accepted: boolean;
  sticker_match: boolean;
  weekly_digest: boolean;
  milestones: boolean;
  unsubscribed_all: boolean;
}

const DEFAULTS: EmailPreferences = {
  invite_accepted: true,
  sticker_match: true,
  weekly_digest: true,
  milestones: true,
  unsubscribed_all: false,
};

export function useEmailPreferences(userId: string | undefined) {
  const [prefs, setPrefs] = useState<EmailPreferences>(DEFAULTS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!userId) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      const { data, error: fetchErr } = await supabase
        .from('email_preferences')
        .select('invite_accepted, sticker_match, weekly_digest, milestones, unsubscribed_all')
        .eq('user_id', userId)
        .maybeSingle();
      if (cancelled) return;
      if (fetchErr) {
        setError(fetchErr.message);
      } else if (data) {
        setPrefs({
          invite_accepted: !!data.invite_accepted,
          sticker_match: !!data.sticker_match,
          weekly_digest: !!data.weekly_digest,
          milestones: !!data.milestones,
          unsubscribed_all: !!data.unsubscribed_all,
        });
      }
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [userId]);

  const update = useCallback(
    async (patch: Partial<EmailPreferences>) => {
      if (!userId) return;
      setSaving(true);
      setError(null);
      const next = { ...prefs, ...patch };
      setPrefs(next);
      const { error: upErr } = await supabase
        .from('email_preferences')
        .upsert({ user_id: userId, ...next }, { onConflict: 'user_id' });
      if (upErr) {
        setError(upErr.message);
      }
      setSaving(false);
    },
    [prefs, userId]
  );

  return { prefs, loading, saving, error, update };
}
